import React, { Suspense, useState } from 'react';
import {
  notification,
  type TablePaginationConfig,
  type TableProps,
} from 'antd';
import { IDoOrder, IDoOrderReq, IDoOrderRes } from '@/interfaces/doOrder';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DeleteFilled, EditFilled } from '@ant-design/icons';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { IDataModal } from '@/interfaces/dataModal';
import { deleteDoOrder } from '@/adapters/doOrder';
import InboundItemTableModal from '@/containers/inbound/components/inbound-item-table-modal';
import { IInboundItem } from '@/interfaces/inbound';

interface IProps {
  showPagging?: boolean;
  loading: boolean;
  criteria: IDoOrderReq;
  doOrder: IDoOrderRes;
  onDelete: (id: number) => void;
  onEditData: (item: IDoOrder) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IDoOrder> | SorterResult<IDoOrder>[],
  ) => void;
}

const _modal: IDataModal<IDoOrder> = {
  open: false,
};

const _items: IDataModal<IInboundItem[]> = {
  open: false,
};

const DoReportTable = ({
  loading,
  criteria,
  doOrder,
  showPagging = true,
  onTableChange,
  onEditData,
  onDelete,
}: IProps) => {
  dayjs.extend(relativeTime);

  const [deleteModal, setDeleteModal] = useState<IDataModal<IDoOrder>>({
    ..._modal,
  });

  const [items, setItems] = useState<IDataModal<IInboundItem[]>>({
    ..._items,
  });

  const columns: TableProps<IDoOrder>['columns'] = [
    {
      title: 'CREATED DATE',
      dataIndex: 'createDate',
      key: 'createDate',
      align: 'center',
      render: _ => formatDate(_),
      width: 140,
    },
    {
      title: 'DO NUMBER',
      dataIndex: 'code',
      key: 'code',
      align: 'center',
      className: 'font-bold text-blue-700',
      width: 120,
      render: (_, rec) => (
        <div
          className="underline underline-offset-2 cursor-pointer"
          onClick={() => setItems({ open: true, item: rec.inboundItems })}
        >
          {_}
        </div>
      ),
    },
    {
      title: 'INV NUMBER',
      dataIndex: 'ivNo',
      key: 'ivNo',
      render: (_, rec) => rec?.inboundItems?.map(c => c.invNO).join(', '),
    },
    {
      title: 'ITEM CODE',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold',
      align: 'center',
      width: 160,
    },
    {
      title: 'ITEM NAME',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
    },
    {
      title: 'VOLUMN',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold',
      align: 'right',
      width: 120,
      render: _ => formatNumber(_),
    },
    {
      title: 'REQUEST ETD SIN',
      dataIndex: 'requestEtdSin',
      key: 'requestEtdSin',
      align: 'center',
      render: _ => formatDate(_),
      width: 140,
    },
    {
      title: 'REQUEST ETA BKK',
      dataIndex: 'requestEtaBkk',
      key: 'requestEtaBkk',
      align: 'center',
      render: _ => formatDate(_),
      width: 140,
    },
    {
      title: 'UPDATED ETD SIN',
      dataIndex: 'updateEtdSin',
      key: 'updateEtdSin',
      align: 'center',
      render: _ => formatDate(_),
      width: 140,
    },
    {
      title: 'UPDATED ETA BKK',
      dataIndex: 'updateEtaBkk',
      key: 'updateEtaBkk',
      align: 'center',
      render: _ => formatDate(_),
      width: 140,
    },
    {
      title: 'REMAIN',
      dataIndex: 'remain',
      key: 'remain',
      className: 'font-bold',
      align: 'right',
      width: 120,
      render: _ => (_ && formatNumber(_)) || 'RECEIVED',
    },
    {
      title: '',
      dataIndex: 'code',
      key: 'isConfirm',
      className: 'font-medium',
      align: 'center',
      width: 40,
      render: (_, rec) => {
        return (
          <div className="flex flex-row divide-x divide-dashed items-center w-full justify-end">
            {(rec.remain && (
              <DeleteFilled
                className="text-red-700 px-1.5"
                onClick={() =>
                  setDeleteModal({
                    open: true,
                    item: rec,
                    id: rec.id,
                  })
                }
              />
            )) ||
              ''}
            <EditFilled
              className="text-blue-500 px-1.5"
              onClick={() => onEditData(rec)}
            />
          </div>
        );
      },
    },
  ];

  const onDeleteCustomer = async () => {
    try {
      await deleteDoOrder(Number(deleteModal?.id));
      notification.success({
        message: 'ลบ DO สำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._modal });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IDoOrder>
        columns={columns}
        criteria={criteria}
        data={doOrder}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1500}
        scroll
        showPagination={showPagging}
      />
      <Suspense>
        <DeleteConfirmModal
          content={`คุณต้องการลบลูก DO '${deleteModal?.item?.code}' ใช่หรือไม่`}
          handleCancel={() => {
            setDeleteModal({ ..._modal });
          }}
          handleOk={() => {
            onDeleteCustomer();
          }}
          open={deleteModal?.open}
          title={`DO '${deleteModal?.item?.code}'`}
        />
        <InboundItemTableModal
          dataModal={items}
          handleOk={function (): void {
            setItems({ ..._items });
            onDelete(0);
          }}
          handleCancel={function (): void {
            setItems({ ..._items });
            onDelete(0);
          }}
        />
      </Suspense>
    </div>
  );
};

export default DoReportTable;
